import { useCallback, useEffect, useMemo, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { Api, Submissions, Senders, Upload } from '../api'
import type {
  CurrentReading,
  StatsAccuracy,
  SenderSummary,
  StatsBucket,
  StatsResponse,
  SubmissionDetail,
  SubmissionSummary,
} from '../api'
import { UserType } from '../api'
import CorrectionForm from '../components/CorrectionForm'
import AppHeader from '../components/AppHeader'
import { commonName } from '../speciesCommonNames'
import { buildSubmissionsHash, EMPTY_SUBMISSIONS_STATE, parseSubmissionsHash, type SubmissionsHashState } from '../lib/submissionsUrl'

interface Props {
  onNavigate: (target: string) => void
  onLogout: () => void
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Analyzing…',
  replied: 'Replied',
  no_size: 'No size',
  no_fish: 'No fish',
  no_media: 'No photo',
  non_image_media: 'Not an image',
  undecodable: 'Unreadable',
  declined_unknown: 'Declined',
  failed: 'Failed',
}
const STATUS_ORDER = ['pending', 'replied', 'no_size', 'no_fish', 'declined_unknown', 'no_media', 'non_image_media', 'undecodable', 'failed']
const CHANNEL_LABEL: Record<string, string> = { sms: 'SMS', whatsapp_member: 'WhatsApp (team)', whatsapp_direct: 'WhatsApp', upload: 'Upload' }

const inches = (mm: number | null | undefined) => (mm == null ? null : `${(Math.round((mm / 25.4) * 2) / 2).toString()} in`)
const pounds = (g: number) => (Math.round((g / 453.592) * 2) / 2).toString()
const pct = (x: number) => `${Math.round(x * 100)}%`
const fmtWhen = (d: Date) =>
  d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const speciesLabel = (sci: string | null | undefined, common?: string | null) =>
  sci ? `${common ?? commonName(sci) ?? sci}` : null

const SOURCE_TAG: Record<string, string> = { text: 'texted', form: 'entered' }

/** One line per fish: species, then sizes; a field reads `est → cur` with its
 *  source only where the current reading differs from our estimate. */
function readingLine(r: CurrentReading): string | null {
  if (r.notFish) return null  // the state chip says it
  const parts: string[] = []
  const sp = r.species
  const spCur = sp.common ?? sp.scientific
  if (spCur) {
    parts.push(r.changed.includes('species')
      ? `${sp.estimateCommon ?? sp.estimateScientific ?? '?'} → ${spCur} (${SOURCE_TAG[sp.source]})`
      : spCur)
  }
  const num = (name: 'length' | 'girth' | 'weight', fmt: (v: number) => string, prefix = '') => {
    const f = r[name]
    if (f.value == null) return
    parts.push(r.changed.includes(name) && f.estimate != null
      ? `${prefix}${fmt(f.estimate)} → ${fmt(f.value)} (${SOURCE_TAG[f.source]})`
      : `${prefix}${fmt(f.value)}`)
  }
  num('length', v => inches(v)!)
  num('girth', v => inches(v)!, 'girth ')
  num('weight', v => `${pounds(v)} lb`, '~')
  return parts.length ? parts.join(' · ') : null
}

const STATE_LABEL: Record<string, string> = {
  unchecked: 'unchecked', confirmed: 'confirmed', corrected: 'corrected', disputed: 'disputed', not_fish: 'not a fish',
}
function stateClass(state: string): string {
  switch (state) {
    case 'corrected': return 'bg-amber-50 text-amber-900 border-amber-200'
    case 'confirmed': return 'bg-emerald-50 text-emerald-900 border-emerald-200'
    case 'disputed': return 'bg-rose-50 text-rose-900 border-rose-200'
    case 'not_fish': return 'bg-rose-50 text-rose-900 border-rose-200'
    default: return 'bg-stone-100 text-stone-500 border-stone-200'
  }
}

function statusClass(status: string): string {
  switch (status) {
    case 'replied': return 'bg-emerald-100 text-emerald-800'
    case 'no_size': return 'bg-amber-100 text-amber-800'
    case 'no_fish': return 'bg-rose-100 text-rose-800'
    case 'declined_unknown': return 'bg-stone-200 text-stone-700'
    default: return 'bg-stone-100 text-stone-600'
  }
}

export default function SubmissionsPage({ onNavigate, onLogout }: Props) {
  const [hashState, setHashState] = useState<SubmissionsHashState>(() => parseSubmissionsHash(window.location.hash))
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [rows, setRows] = useState<SubmissionSummary[]>([])
  const [nextBefore, setNextBefore] = useState<Date | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<SubmissionDetail | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    Api.getCurrentUser().then(r => setIsAdmin(r.user.type === UserType.Admin)).catch(() => {})
  }, [])

  // URL is the source of truth for filters + open detail.
  useEffect(() => {
    const onHash = () => setHashState(parseSubmissionsHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const update = useCallback((patch: Partial<SubmissionsHashState>) => {
    const next = { ...parseSubmissionsHash(window.location.hash), ...patch }
    window.history.pushState({}, '', buildSubmissionsHash(next))
    setHashState(next)
  }, [])

  useEffect(() => {
    Submissions.getStats({ body: {} }).then(setStats).catch(e => setError(String(e)))
  }, [])

  const listRequest = useMemo(() => ({
    statuses: hashState.statuses.length ? (hashState.statuses as never[]) : undefined,
    channels: hashState.channel ? ([hashState.channel] as never[]) : undefined,
    fromNumbers: hashState.sender ? [hashState.sender] : undefined,
    species: hashState.species ? [hashState.species] : undefined,
    hasFeedback: hashState.feedback === 'any' ? undefined : hashState.feedback === 'with',
    start: hashState.start ? new Date(hashState.start) : undefined,
    end: hashState.end ? new Date(`${hashState.end}T23:59:59`) : undefined,
    limit: 30,
  }), [hashState.statuses, hashState.channel, hashState.sender, hashState.species, hashState.feedback, hashState.start, hashState.end])

  const load = useCallback(async (before: Date | null) => {
    setLoading(true)
    try {
      const r = await Submissions.listSubmissions({ submissionListRequest: { ...listRequest, before: before ?? undefined } })
      setRows(prev => (before ? [...prev, ...r.submissions] : r.submissions))
      setNextBefore(r.nextBefore ?? null)
      setError(null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [listRequest])
  useEffect(() => { void load(null) }, [load])

  // Deep link / card click → detail
  const refreshDetail = useCallback(async (id: string) => {
    const r = await Submissions.getSubmissions({ submissionGetRequest: { ids: [id] } })
    const fresh = r.submissions[0]
    if (!fresh) return
    setDetail(fresh)
    setRows(prev => prev.map(row => (row.id === fresh.id ? { ...row, ...fresh } : row)))
  }, [])

  useEffect(() => {
    if (!hashState.id) { setDetail(null); return }
    let cancelled = false
    Submissions.getSubmissions({ submissionGetRequest: { ids: [hashState.id] } })
      .then(r => { if (!cancelled) setDetail(r.submissions[0] ?? null) })
      .catch(e => setError(String(e)))
    return () => { cancelled = true }
  }, [hashState.id])

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader active="submissions" onNavigate={onNavigate} onLogout={onLogout} />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}

        {stats && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Bucket title="All time" b={stats.allTime} />
            <Bucket title="Last 7 days" b={stats.last7Days} />
          </section>
        )}

        {isAdmin && <UploadersAdmin />}

        <section className="bg-white border border-stone-200 rounded-lg p-3 flex flex-wrap gap-3 items-end text-sm">
          <Field label="Outcome">
            <select multiple value={hashState.statuses} size={3}
              onChange={e => update({ statuses: Array.from(e.target.selectedOptions).map(o => o.value) })}
              className="border border-stone-300 rounded px-2 py-1 text-sm min-w-[9rem]">
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </Field>
          <Field label="Feedback">
            <select value={hashState.feedback} onChange={e => update({ feedback: e.target.value as SubmissionsHashState['feedback'] })}
              className="border border-stone-300 rounded px-2 py-1 text-sm">
              <option value="any">Any</option><option value="with">With feedback</option><option value="without">Without</option>
            </select>
          </Field>
          <Field label="Sender">
            <select value={hashState.sender ?? ''} onChange={e => update({ sender: e.target.value || null })}
              className="border border-stone-300 rounded px-2 py-1 text-sm max-w-[14rem]">
              <option value="">All</option>
              {stats?.senders.map(s => <option key={s.fromNumber} value={s.fromNumber}>{s.name ?? s.fromNumber} ({s.count})</option>)}
            </select>
          </Field>
          <Field label="Species">
            <select value={hashState.species ?? ''} onChange={e => update({ species: e.target.value || null })}
              className="border border-stone-300 rounded px-2 py-1 text-sm max-w-[14rem]">
              <option value="">All</option>
              {stats?.species.map(s => <option key={s} value={s}>{commonName(s) ?? s}</option>)}
            </select>
          </Field>
          <Field label="Channel">
            <select value={hashState.channel ?? ''} onChange={e => update({ channel: e.target.value || null })}
              className="border border-stone-300 rounded px-2 py-1 text-sm">
              <option value="">All</option>
              {Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="From">
            <input type="date" value={hashState.start ?? ''} onChange={e => update({ start: e.target.value || null })}
              className="border border-stone-300 rounded px-2 py-1 text-sm" />
          </Field>
          <Field label="To">
            <input type="date" value={hashState.end ?? ''} onChange={e => update({ end: e.target.value || null })}
              className="border border-stone-300 rounded px-2 py-1 text-sm" />
          </Field>
          <button onClick={() => update({ ...EMPTY_SUBMISSIONS_STATE, id: null })}
            className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 hover:text-bear px-2 py-1">
            Clear
          </button>
        </section>

        <section className="space-y-3">
          {rows.map(s => (
            <button key={s.id} onClick={() => update({ id: s.id })}
              className="w-full text-left bg-white border border-stone-200 rounded-lg overflow-hidden flex flex-col sm:flex-row hover:border-bear/60 transition-colors">
              <div className="sm:w-64 h-48 bg-stone-200 flex-shrink-0">
                {s.photoUrl
                  ? <img src={s.photoUrl} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs font-mono">no photo</div>}
              </div>
              <div className="p-4 flex-1 min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold">{s.senderName ?? `UNKNOWN ${s.fromNumber}`}</span>
                  <span className="text-stone-500">{fmtWhen(s.receivedAt)}</span>
                  <span className="font-mono text-xs text-stone-500">#{s.code}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${statusClass(s.status)}`}>
                    {STATUS_LABEL[s.status] ?? s.status}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400">{CHANNEL_LABEL[s.channel] ?? s.channel}</span>
                  {s.capturedAt && <span className="text-[10px] text-stone-400">Taken {fmtWhen(s.capturedAt)}</span>}
                </div>
                {readingLine(s.current) && (
                  <div className="text-sm text-stone-800">{readingLine(s.current)}</div>
                )}
                {(s.correctionCount > 0 || s.current.state !== 'unchecked') && (
                  <div className={`inline-block text-xs border rounded px-2 py-1 ${stateClass(s.current.state)}`}>
                    {s.current.verdict === 'agree' && '👍 '}
                    {s.current.verdict === 'disagree' && '👎 '}
                    {STATE_LABEL[s.current.state]}
                    {s.feedback && s.current.state === 'unchecked' && <span className="ml-1 text-stone-400">· {s.feedback.text}</span>}
                  </div>
                )}
              </div>
            </button>
          ))}
          {!loading && rows.length === 0 && <div className="text-sm text-stone-500 text-center py-8">Nothing matches.</div>}
          {nextBefore && (
            <button disabled={loading} onClick={() => void load(nextBefore)}
              className="w-full py-2 text-sm text-stone-600 bg-white border border-stone-200 rounded hover:border-bear/60 disabled:opacity-50">
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </section>
      </main>

      <Dialog open={detail != null} onClose={() => update({ id: null })} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="mx-auto max-w-5xl">
            {detail && (
              <Dialog.Panel className="bg-white rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-start justify-between p-4 border-b border-stone-200">
                  <div>
                    <Dialog.Title className="font-semibold">
                      {detail.senderName ?? `UNKNOWN ${detail.fromNumber}`} <span className="font-mono text-stone-500">#{detail.code}</span>
                    </Dialog.Title>
                    <div className="text-xs text-stone-500">{detail.receivedAt.toLocaleString()} · {CHANNEL_LABEL[detail.channel] ?? detail.channel} · {STATUS_LABEL[detail.status]}</div>
                  </div>
                  <button onClick={() => update({ id: null })} className="text-stone-400 hover:text-stone-700 text-xl leading-none">×</button>
                </div>
                {detail.photoUrl && <img src={detail.photoUrl} alt="" className="w-full max-h-[60vh] object-contain bg-stone-900" />}
                {detail.siblings.length > 0 && (
                  <div className="px-4 pt-3 flex flex-wrap gap-2 text-xs">
                    <span className="text-stone-500">Same message:</span>
                    {detail.siblings.map(id => (
                      <button key={id} onClick={() => update({ id })} className="font-mono px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200">{id.slice(0, 8)}</button>
                    ))}
                  </div>
                )}
                {detail.batch && (
                  <div className="px-4 pt-3 text-xs">
                    <a href={`/upload#batch=${encodeURIComponent(detail.batch.batchId)}`}
                      className="text-bear hover:underline">
                      Batch of {detail.batch.expectedCount} by {detail.batch.senderName ?? 'unknown'} — Open batch report
                    </a>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
                  <div className="space-y-4">
                    <Section title="Reading">
                      <ReadingTable r={detail.current} weightBand={detail.weightLowG && detail.weightHighG ? `${pounds(detail.weightLowG)}–${pounds(detail.weightHighG)} lb` : null} />
                      <div className="border border-stone-200 rounded p-3 mt-3">
                        <h4 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-2">
                          Edit current reading
                        </h4>
                        <CorrectionForm
                          key={detail.id + detail.correctionCount}
                          submissionId={detail.id}
                          reading={detail.current}
                          onSaved={() => void refreshDetail(detail.id)}
                        />
                      </div>
                    </Section>
                    <Section title="What we replied">
                      <pre className="whitespace-pre-wrap font-sans text-sm bg-stone-50 border border-stone-200 rounded p-3">{detail.replyText ?? '—'}</pre>
                    </Section>
                    <details className="group">
                      <summary className="cursor-pointer font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 hover:text-stone-800">
                        Corrections ({detail.corrections.length})
                      </summary>
                      <div className="mt-2 space-y-2">
                      {detail.corrections.length === 0 && <div className="text-sm text-stone-500">None yet.</div>}
                      {detail.corrections.map(c => (
                        <div key={c.id} className="border border-stone-200 rounded p-3 text-sm space-y-1">
                          <div className="flex justify-between text-xs text-stone-500">
                            <span>{c.receivedAt.toLocaleString()}</span>
                            <span className="flex items-center gap-2">
                              <span className="font-mono uppercase px-1.5 py-0.5 rounded bg-stone-100">
                                {c.source === 'form' ? 'entered' : c.source === 'reaction' ? 'reaction' : 'texted'}
                              </span>
                              <span className="font-mono uppercase">{c.resolution.replace('_', ' ')}</span>
                            </span>
                          </div>
                          {c.source !== 'reaction' && c.rawText && <div className="italic">“{c.rawText}”</div>}
                          <div className="text-stone-700">
                            {c.source === 'reaction' ? (
                              <>{c.verdict === 'agree' ? '👍' : c.verdict === 'disagree' ? '👎' : '◦'} {c.reaction}</>
                            ) : c.isFeedback === null ? 'extraction failed' : c.isFeedback === false ? 'chatter' : [
                              c.verdict && (c.verdict === 'agree' ? '👍 agreed' : '👎 disagreed'),
                              c.lengthMm && `length ${inches(c.lengthMm)}`,
                              c.girthMm && `girth ${inches(c.girthMm)}`,
                              c.weightG && `${pounds(c.weightG)} lb`,
                              c.species && `species “${c.species}”${c.speciesScientific ? ` → ${c.speciesScientific}` : ''}`,
                              c.note && `note: ${c.note}`,
                            ].filter(Boolean).join(' · ') || 'confirmed, nothing new'}
                          </div>
                          {c.confirmText && <div className="text-xs text-stone-500">We said: {c.confirmText}</div>}
                        </div>
                      ))}
                      </div>
                    </details>
                  </div>
                  <div className="space-y-4">
                    <Section title="Diagnostics">
                      <dl className="text-sm grid grid-cols-[8rem_1fr] gap-y-1">
                        <dt className="text-stone-500">Fish detector</dt>
                        <dd>{detail.diagnostics ? (detail.diagnostics.segmentationFound ? 'found' : 'nothing found') : '—'}</dd>
                        <dt className="text-stone-500">Classifier</dt>
                        <dd>{detail.diagnostics?.species?.length
                          ? detail.diagnostics.species.map(c => `${c.commonName ?? commonName(c.name) ?? c.name} ${c.score.toFixed(2)}`).join(', ')
                          : (detail.speciesScientific ? `${speciesLabel(detail.speciesScientific, detail.speciesCommon)} ${detail.speciesScore?.toFixed(2) ?? ''}` : 'not run')}</dd>
                        <dt className="text-stone-500">Agreement</dt>
                        <dd>{detail.sizeConfidence ?? '—'}{detail.diagnostics?.size?.lowReason ? ` — ${detail.diagnostics.size.lowReason}` : ''}</dd>
                        <dt className="text-stone-500">Weight band</dt>
                        <dd>{detail.weightLowG && detail.weightHighG ? `${pounds(detail.weightLowG)}–${pounds(detail.weightHighG)} lb` : '—'}</dd>
                      </dl>
                      <table className="w-full text-sm mt-3">
                        <thead><tr className="text-left text-xs text-stone-500"><th className="py-1">Model</th><th>Length</th><th>Girth</th><th>Conf.</th><th>Basis</th></tr></thead>
                        <tbody>
                          {Object.entries(detail.estimates).map(([p, r]) => (
                            <tr key={p} className="border-t border-stone-100 align-top">
                              <td className="py-1 font-mono text-xs">{p}</td>
                              {r.status === 'ok' && r.estimate ? (
                                <>
                                  <td>{r.estimate.fishPresent ? (inches(r.estimate.lengthMm) ?? 'declined') : 'no fish'}</td>
                                  <td>{inches(r.estimate.girthMm) ?? '—'}</td>
                                  <td>{r.estimate.confidence}</td>
                                  <td className="text-xs text-stone-600">{r.estimate.basis}</td>
                                </>
                              ) : <td colSpan={4} className="text-xs text-rose-700">failed: {r.error ?? 'no result'}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Section>
                    <Section title="Records">
                      <dl className="text-xs font-mono grid grid-cols-[7rem_1fr] gap-y-1 break-all text-stone-600">
                        <dt>s3 prefix</dt><dd>{detail.s3Prefix}</dd>
                        {detail.origin.messageSid && <><dt>message sid</dt><dd>{detail.origin.messageSid} (#{detail.origin.index})</dd></>}
                        {detail.origin.batchId && <><dt>batch</dt><dd>{detail.origin.batchId} (#{detail.origin.index})</dd></>}
                        <dt>reply sid</dt><dd>{detail.replyMessageSid ?? '—'}</dd>
                        {detail.conversationSid && <><dt>conversation</dt><dd>{detail.conversationSid}</dd></>}
                        <dt>submission</dt><dd>{detail.id}</dd>
                      </dl>
                    </Section>
                  </div>
                </div>
              </Dialog.Panel>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{label}</span>
      {children}
    </label>
  )
}

function ReadingTable({ r, weightBand }: { r: CurrentReading; weightBand: string | null }) {
  const when = (at: Date | null) => (at ? at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '')
  const rows: { name: string; est: string; cur: string; source: string; at: Date | null; changed: boolean }[] = [
    {
      name: 'Species',
      est: r.species.estimateCommon ?? r.species.estimateScientific ?? '—',
      cur: r.notFish ? 'not a fish' : (r.species.common ?? r.species.scientific ?? '—'),
      source: r.species.source, at: r.species.at, changed: r.changed.includes('species'),
    },
    ...([['Length', r.length, (v: number) => inches(v)!], ['Girth', r.girth, (v: number) => inches(v)!], ['Weight', r.weight, (v: number) => `${pounds(v)} lb`]] as const).map(([name, f, fmt]) => ({
      name,
      est: name === 'Weight' && weightBand ? weightBand : f.estimate != null ? fmt(f.estimate) : '—',
      cur: r.notFish ? '—' : f.value != null ? fmt(f.value) : '—',
      source: f.source, at: f.at, changed: r.changed.includes(name.toLowerCase() as 'length' | 'girth' | 'weight'),
    })),
  ]
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs text-stone-500"><th className="py-1 font-normal">Field</th><th className="font-normal">Our estimate</th><th className="font-normal">Current</th><th className="font-normal">Source</th></tr></thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.name} className={`border-t border-stone-100 ${row.changed ? 'bg-amber-50' : ''}`}>
            <td className="py-1 text-stone-500">{row.name}</td>
            <td className={row.changed ? 'line-through text-stone-400' : ''}>{row.est}</td>
            <td className={row.changed ? 'font-semibold text-amber-900' : ''}>{row.cur}</td>
            <td className="text-xs text-stone-500">{row.source === 'estimate' ? 'our estimate' : `${SOURCE_TAG[row.source]} ${when(row.at)}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{title}</h3>
      {children}
    </div>
  )
}

function Bucket({ title, b }: { title: string; b: StatsBucket }) {
  const statuses = Object.entries(b.byStatus).sort((x, y) => STATUS_ORDER.indexOf(x[0]) - STATUS_ORDER.indexOf(y[0]))
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
      <h2 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{title}</h2>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Submissions" value={b.submissions} sub={statuses.map(([s, n]) => `${n} ${STATUS_LABEL[s] ?? s}`).join(' · ')} />
        <Stat label="Senders" value={b.senders} sub={`${b.guideSenders} guides · ${b.teamSenders} team`} />
        <Stat label="With feedback" value={b.withFeedback} sub={`${b.feedbackMeasurement} measured · ${b.feedbackConfirmed} confirmed · ${b.feedbackAgreed} 👍 · ${b.feedbackDisagreed} 👎 · ${b.feedbackChatter} other`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Acc label="Length" a={b.length} />
        <Acc label="Girth" a={b.girth} />
        <Acc label="Weight" a={b.weight} />
        <Stat label="Species" value={b.speciesAgreement ? pct(b.speciesAgreement.agreed / b.speciesAgreement.compared) : '—'}
          sub={b.speciesAgreement ? `${b.speciesAgreement.agreed}/${b.speciesAgreement.compared} agreed` : 'no comparisons'} />
      </div>
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub && <div className="text-[11px] text-stone-500 leading-tight">{sub}</div>}
    </div>
  )
}

function Acc({ label, a }: { label: string; a: StatsAccuracy | null | undefined }) {
  return (
    <Stat label={`${label} error`} value={a ? pct(a.medianAbsPct) : '—'}
      sub={a ? `median of ${a.n} · ${pct(a.within10Pct)} ≤10% · ${pct(a.within20Pct)} ≤20%` : 'no feedback yet'} />
  )
}

function UploadersAdmin() {
  const [senders, setSenders] = useState<SenderSummary[]>([])
  const [paused, setPaused] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [busyPhone, setBusyPhone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    Senders.listSenders({ body: {} }).then(r => setSenders(r.senders)).catch(e => setError(String(e)))
    Upload.uploadStatus({ body: {} }).then(r => setPaused(!r.enabled)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  async function addAndInvite(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await Senders.upsertSender({ senderUpsertRequest: { phone, name, active: true } })
      await Senders.inviteSender({ senderInviteRequest: { phone } })
      setName(''); setPhone('')
      load()
    } catch (e) {
      setError(String(e))
    }
  }

  async function toggleActive(s: SenderSummary) {
    setBusyPhone(s.phone)
    try {
      await Senders.upsertSender({ senderUpsertRequest: { phone: s.phone, name: s.name, note: s.note ?? undefined, active: !s.active } })
      load()
    } finally {
      setBusyPhone(null)
    }
  }

  async function resendInvite(s: SenderSummary) {
    setBusyPhone(s.phone)
    try {
      await Senders.inviteSender({ senderInviteRequest: { phone: s.phone } })
    } finally {
      setBusyPhone(null)
    }
  }

  async function togglePaused() {
    const next = !paused
    setPaused(next)
    try {
      await Upload.setUploadEnabled({ uploadsSetEnabledRequest: { enabled: !next } })
    } catch (e) {
      setPaused(!next)
      setError(String(e))
    }
  }

  return (
    <section className="bg-white border border-stone-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">Uploaders</h2>
        <button onClick={togglePaused}
          className={`font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded border ${paused ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
          {paused ? 'Uploads paused — resume' : 'Uploads enabled — pause'}
        </button>
      </div>
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
      <form onSubmit={addAndInvite} className="flex flex-wrap gap-2 items-end text-sm">
        <Field label="Name">
          <input value={name} onChange={e => setName(e.target.value)} required
            className="border border-stone-300 rounded px-2 py-1 text-sm" />
        </Field>
        <Field label="Phone">
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+14155550100" required
            className="border border-stone-300 rounded px-2 py-1 text-sm" />
        </Field>
        <button type="submit" className="font-mono text-[10px] tracking-[0.15em] uppercase bg-bear text-white px-3 py-1.5 rounded hover:bg-bear-light">
          Add &amp; invite
        </button>
      </form>
      <div className="space-y-1">
        {senders.map(s => (
          <div key={s.id} className="flex items-center gap-3 text-sm py-1 border-t border-stone-100 first:border-t-0">
            <span className="flex-1">{s.name} <span className="text-stone-400 font-mono text-xs">{s.phone}</span></span>
            <button disabled={busyPhone === s.phone} onClick={() => toggleActive(s)}
              className={`font-mono text-[10px] uppercase px-2 py-0.5 rounded ${s.active ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-600'}`}>
              {s.active ? 'active' : 'disabled'}
            </button>
            <button disabled={busyPhone === s.phone} onClick={() => resendInvite(s)}
              className="font-mono text-[10px] uppercase text-stone-500 hover:text-bear">
              Resend invite
            </button>
          </div>
        ))}
        {senders.length === 0 && <div className="text-sm text-stone-500">No uploaders yet.</div>}
      </div>
    </section>
  )
}
